<script>
    import { toHTML, uriLooksSafe } from '@portabletext/to-html';
    import htm from 'htm';
    import vhtml from 'vhtml';

    export let data;
    export let alignment = 'text-left';
    export let width = 'Normal';

    // set section device display
    let bodyWidth = '';
    switch (width) {
        case 'Normal':
            bodyWidth = '';
            break;
        case 'Tight':
            bodyWidth = 'mx-auto max-w-[23rem] px-2 lg:max-w-xl';
            break;
        default:
            bodyWidth = '';
            break;
    }

    const html = htm.bind(vhtml);

    const customBlockComponents = {
        marks: {
            annotationLinkExternal: (args) => {
                const { value, children } = args;
                const href = value?.url || '';
                const style = value?.linkStyle || 'Normal';

                // set link styles
                let linkStyle = '';
                switch (style) {
                    case 'Normal':
                        linkStyle = 'font-semibold text-primary dark:text-purple-300';
                        break;
                    case 'Button':
                        linkStyle = 'inline-block rounded-xl bg-primary py-2 px-4 mt-1.5 text-center font-semibold text-white mx-2';
                        break;
                    case 'Plain':
                        linkStyle = '';
                        break;
                    default:
                        linkStyle = 'font-semibold text-primary';
                        break;
                }

                if (uriLooksSafe(href)) {
                    const rel = href.startsWith('/') ? undefined : 'noreferrer noopener';
                    const target = value?.newWindow ? '_blank' : '';
                    return html`<a href="${href}" class="${linkStyle}" rel="${rel}" target="${target}">${children}</a>`;
                }
                return children;
            },
            annotationLinkRelative: (args) => {
                const { value, children } = args;
                const href = value?.url || '';
                const style = value?.linkStyle || 'Normal';

                // set link styles
                let linkStyle = '';
                switch (style) {
                    case 'Normal':
                        linkStyle = 'font-semibold text-primary dark:text-purple-300';
                        break;
                    case 'Button':
                        linkStyle = 'inline-block rounded-xl bg-primary py-2 px-4 mt-1.5 text-center font-semibold text-white mx-2';
                        break;
                    case 'Plain':
                        linkStyle = '';
                        break;
                    default:
                        linkStyle = 'font-semibold text-primary';
                        break;
                }

                if (uriLooksSafe(href)) {
                    const target = value?.newWindow ? '_blank' : '';
                    return html`<a href="${href}" class="${linkStyle}" target="${target}">${children}</a>`;
                }
                return children;
            },
            annotationLinkInternal: (args) => {
                const { value, children } = args;
                const href = value?.url || '';
                const style = value?.linkStyle || 'Normal';

                // set link styles
                let linkStyle = '';
                switch (style) {
                    case 'Normal':
                        linkStyle = 'font-semibold text-primary dark:text-purple-300';
                        break;
                    case 'Button':
                        linkStyle = 'inline-block rounded-xl bg-primary py-2 px-4 mt-1.5 text-center font-semibold text-white mx-2';
                        break;
                    case 'Plain':
                        linkStyle = '';
                        break;
                    default:
                        linkStyle = 'font-semibold text-primary';
                        break;
                }

                if (uriLooksSafe(href)) {
                    const target = value?.newWindow ? '_blank' : '';
                    return html`<a href="${href}" class="${linkStyle}" target="${target}">${children}</a>`;
                }
                return children;
            },
            annotationLinkEmail: (args) => {
                const { value, children } = args;
                const email = value?.email || '';
                const style = value?.linkStyle || 'Normal';

                // set link styles
                let linkStyle = '';
                switch (style) {
                    case 'Normal':
                        linkStyle = 'font-semibold text-primary dark:text-purple-300';
                        break;
                    case 'Button':
                        linkStyle = 'inline-block rounded-xl bg-primary py-2 px-4 mt-1.5 text-center font-semibold text-white mx-2';
                        break;
                    case 'Plain':
                        linkStyle = '';
                        break;
                    default:
                        linkStyle = 'font-semibold text-primary';
                        break;
                }

                if (uriLooksSafe(email)) {
                    return html`<a href="mailto:${email}" class="${linkStyle}">${children}</a>`;
                }
                return children;
            }
        },
        types: {
            imageLink: ({ value }) => {
                // if link
                if (value?.link?.url && uriLooksSafe(value?.link?.url)) {
                    const rel = value?.link?.url.startsWith('/') ? undefined : 'noreferrer noopener';
                    const target = value?.newWindow ? '_blank' : '';
                    return html`<a href="${value?.link?.url}" rel="${rel}" target="${target}" class="mb-4 block overflow-hidden rounded-xl"
                        ><img src="${value?.image?.url}?h=700" alt="${value?.alt}" width="100%" height="auto"
                    /></a>`;
                }
                return html`<div class="mb-4 block overflow-hidden rounded-xl">
                    <img src="${value?.image?.url}?h=700" alt="${value?.alt}" width="100%" height="auto" />
                    <div></div>
                </div>`;
            },
            table: ({ value }) => {
                const rows = value.rows;
                let headerRow = [];
                let bodyRows = [];

                rows.forEach((row, index) => {
                    let tr = [];
                    row.cells.forEach((cell) => {
                        if (cell) tr.push(html`<td>${cell}</td>`);
                    });

                    if (index === 0 && tr.length) {
                        // Header row
                        headerRow = html`<tr>
                            ${tr}
                        </tr>`;
                    } else if (index !== 0 && tr) {
                        // Body rows
                        bodyRows.push(
                            html`<tr>
                                ${tr}
                            </tr>`
                        );
                    }
                });

                const table = html`
                    <table>
                        <thead>
                            ${headerRow}
                        </thead>
                        <tbody>
                            ${bodyRows}
                        </tbody>
                    </table>
                `;

                return table;
            },
            lineBreak: ({ value }) => {
                let lineBreak = '';
                switch (value?.style) {
                    case 'Full Line Break':
                        lineBreak = html`<hr class="my-10 border-color-text/10" />`;
                        break;
                    case 'Short Line Break':
                        lineBreak = html`<hr class="m-auto my-14 w-40 border-2 border-color-text/10" />`;
                        break;
                    case 'Spacing Break':
                        lineBreak = html`<hr class="my-10 border-color-text/0" />`;
                        break;
                    default:
                        lineBreak = html`<hr class="my-10 border-color-text/10" />`;
                        break;
                }
                return lineBreak;
            },
            discountCode: ({ value }) => {
                return html`
                    <div class="m-auto flex flex-col justify-center gap-6 p-6 text-center lg:max-w-xl">
                        <div class="text-2xl font-semibold">${value?.title}</div>
                        <p>${value?.description}</p>
                        <div class="border-4 border-dashed border-primary p-6 text-2xl font-semibold dark:border-purple-300">${value?.discountCode}</div>
                    </div>
                `;
            }
        }
    };

    let htmlFromBlocks = toHTML(data, { components: customBlockComponents });

    // clean spam charchters from descriptions
    htmlFromBlocks = htmlFromBlocks.replace(/Â|â€™/g, '');
</script>

<div
    class="
    port-text
    prose-h1:pb-2
    prose-h1:text-2xl
    prose-h1:font-semibold

    prose-h2:pb-2
    prose-h2:text-xl
    prose-h2:font-semibold
    
    prose-h2:first-of-type:mt-0
    prose-h3:pb-2
    prose-h3:text-base
    prose-h3:font-semibold

    prose-h4:text-base
    prose-h4:font-semibold

    prose-p:mb-4
    prose-p:text-base

    prose-blockquote:my-4
    prose-blockquote:rounded-xl
    prose-blockquote:bg-gray-300
    prose-blockquote:p-4
    prose-strong:font-semibold
   
    
    prose-em:italic
    prose-ol:mb-4

    prose-ol:list-inside
    prose-ol:list-decimal
    prose-ul:mb-4

    prose-ul:list-inside
    prose-ul:list-disc
    prose-table:mb-2

    prose-table:w-full
    prose-table:max-w-2xl
    prose-table:leading-normal
    
    prose-thead:px-5
    prose-thead:py-3
    prose-thead:text-left
    prose-thead:text-xs
    prose-thead:font-semibold

    prose-td:p-3
    prose-td:pl-0
    prose-img:overflow-hidden

    prose-img:rounded-2xl
    prose-blockquote:dark:bg-base-700
    prose-thead:dark:text-purple-300
    {alignment}
    {bodyWidth}
"
>
    {@html htmlFromBlocks}
</div>
